import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "wouter";
import { Header } from "@/components/layout/header";
import { Footer } from "@/components/layout/footer";
import { StickyMobileBookCta } from "@/components/layout/sticky-mobile-book-cta";
import { SkeletonText } from "@/components/ui/luxury-skeleton";
import { BlogPostCard } from "@/components/blog/blog-post-card";
import { categoryVisual, blogImg } from "@/lib/blog-visuals";
import { ArticleMasthead } from "@/components/blog/article-masthead";
import { Breadcrumbs } from "@/components/layout/breadcrumbs";
import { useSEO, useSEOSettings, generateBreadcrumbSchema, generateCollectionPageSchema, generateItemListSchema } from "@/lib/seo-utils";
import { ChevronLeft, ArrowRight } from "lucide-react";
import type { BlogCategory, BlogPostWithCategory } from "@shared/schema";
import { useBookCta } from "@/hooks/use-book-href";

const categoryDescriptions: Record<string, string> = {
  "beaches-coastal-guides": "Complete guide to Oahu's best beaches - from Waikiki to North Shore. Swimming, snorkeling, sunset spots, and local tips for Hawaii's stunning coastline.",
  "snorkeling-water-activities": "Best snorkeling spots in Oahu including Hanauma Bay, Shark's Cove & hidden gems. Water sports, surfing guides, and underwater adventures in Hawaii.",
  "hiking-outdoor-adventure": "Oahu hiking trails from Diamond Head to Koko Head. Trail guides, difficulty ratings, and safety tips for exploring Hawaii's mountains and rainforests.",
  "food-local-culture": "Authentic Hawaiian food guide - plate lunches, poke bowls, shave ice & local restaurants. Saturday farmers markets and cultural experiences in Oahu.",
  "trip-planning-itineraries": "Oahu vacation itineraries for 3-day, 7-day & 2-week trips. Packing lists, budgeting tips, and insider advice for planning your Hawaii vacation.",
  "transportation-practical-guides": "Getting around Oahu - car rentals, TheBus routes, parking tips & airport transfers. Practical transportation guides for Hawaii visitors.",
};

const categoryKeywords: Record<string, string> = {
  "beaches-coastal-guides": "oahu beaches, best beaches hawaii, waikiki beach, north shore beaches, lanikai beach, kailua beach, hawaii beach guide",
  "snorkeling-water-activities": "snorkeling oahu, hanauma bay, sharks cove, oahu surfing, water sports hawaii, scuba diving oahu, kayaking oahu",
  "hiking-outdoor-adventure": "oahu hiking, diamond head trail, koko head, manoa falls, pillbox hike, hawaii trails, oahu hikes",
  "food-local-culture": "hawaiian food, plate lunch oahu, poke bowls, saturday farmers market oahu, shave ice hawaii, local restaurants oahu",
  "trip-planning-itineraries": "oahu itinerary, hawaii vacation planning, oahu trip guide, what to do oahu, hawaii packing list",
  "transportation-practical-guides": "oahu transportation, car rental hawaii, thebus oahu, honolulu airport, getting around oahu, parking oahu",
};


export default function BlogCategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const { seoSettings } = useSEOSettings();
  const bookCta = useBookCta();

  const { data: category, isLoading: categoryLoading } = useQuery<BlogCategory>({
    queryKey: ['/api/blog/categories', slug],
    queryFn: async () => {
      const response = await fetch(`/api/blog/categories/${slug}`);
      if (!response.ok) {
        if (response.status === 404) throw new Error('Category not found');
        throw new Error('Failed to fetch category');
      }
      return response.json();
    },
    enabled: !!slug,
  });

  const { data: posts, isLoading: postsLoading } = useQuery<BlogPostWithCategory[]>({
    queryKey: ['/api/blog/posts', slug],
    queryFn: async () => {
      const response = await fetch(`/api/blog/posts?category=${slug}`);
      if (!response.ok) throw new Error('Failed to fetch posts');
      return response.json();
    },
    enabled: !!slug,
  });

  const { data: categories } = useQuery<BlogCategory[]>({
    queryKey: ['/api/blog/categories'],
  });

  const description = slug ? categoryDescriptions[slug] : undefined;
  const keywords = slug ? categoryKeywords[slug] : undefined;
  const postCount = posts?.length || 0;

  const seoHead = useSEO({
    pageType: "blog",
    title: category ? `${category.name} | Oahu Travel Guide | The Kualoa Beach House` : "Oahu Travel Guide",
    description: description || category?.description || `Explore our ${category?.name || ''} guides for Oahu, Hawaii.`,
    keywords: keywords || (category ? `${category.name}, Oahu, Hawaii travel, vacation tips` : undefined),
    ogType: "website",
    ogTitle: category ? `${category.name} - Oahu Travel Guide` : "Oahu Travel Guide",
    ogDescription: description || `Discover ${postCount} expert guides for ${category?.name || 'traveling'} in Oahu, Hawaii.`,
    canonical: category ? `https://vacationrentaloahu.co/blog/category/${category.slug}` : undefined,
    twitterCard: "summary_large_image",
    structuredData: seoSettings && category && posts ? [
      generateBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Travel Guide", url: "/blog" },
        { name: category.name, url: `/blog/category/${category.slug}` }
      ]),
      generateCollectionPageSchema(
        category.name,
        category.slug,
        description || category.description || '',
        postCount
      ),
      generateItemListSchema(
        posts.map((post, index) => ({
          name: post.title,
          url: `/blog/${post.slug}`,
          description: post.excerpt,
          position: index + 1
        })),
        `${category.name} - Oahu Travel Guides`
      )
    ] : []
  });

  const isLoading = categoryLoading || postsLoading;

  if (!categoryLoading && !category) {
    return (
      <div className="min-h-screen bg-shell pb-24 lg:pb-0">
        <Header />
        <main className="pt-32 pb-16">
          <div className="container mx-auto max-w-3xl px-6 py-20 text-center">
            <h1 className="mb-4 font-serif text-luxury-2xl font-normal tracking-tight text-forest">
              Category Not Found
            </h1>
            <p className="mb-10 font-sans text-[15px] font-light leading-relaxed text-ink-soft">
              The category you're looking for doesn't exist or has been moved.
            </p>
            <Link
              href="/blog"
              className="inline-flex min-h-[24px] items-center gap-2 border-b border-sand-deep pb-1 font-sans text-[11px] uppercase tracking-[0.24em] text-forest transition-colors hover:border-forest"
              data-testid="link-back-to-blog"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
              Back to Travel Guide
            </Link>
          </div>
        </main>
        <StickyMobileBookCta />
      <Footer />
      </div>
    );
  }

  const otherCategories = categories?.filter((c) => c.slug !== slug) ?? [];

  return (
    <>
    {seoHead}
    <div className="min-h-screen bg-shell pb-24 lg:pb-0">
      <Header />

      <main id="main-content">
        <ArticleMasthead
          eyebrow="Oahu Travel Guide"
          eyebrowHref="/blog"
          title={isLoading ? "—" : category?.name}
          deck={isLoading ? undefined : category?.description || description}
          meta={!isLoading && posts ? [`${posts.length} ${posts.length === 1 ? "article" : "articles"}`] : undefined}
        />

        <div className="container mx-auto max-w-5xl px-6 pt-8">
          {/* The category's photo, so the page opens on an image rather than a list. */}
          {category && (
            <div className="relative aspect-[21/9] w-full overflow-hidden border border-sand-deep">
              <img
                src={blogImg(categoryVisual(category.slug).image, 1400)}
                srcSet={`${blogImg(categoryVisual(category.slug).image, 800)} 800w, ${blogImg(categoryVisual(category.slug).image, 1400)} 1400w, ${blogImg(categoryVisual(category.slug).image, 2000)} 2000w`}
                sizes="(min-width: 1024px) 1024px, 100vw"
                alt={categoryVisual(category.slug).alt}
                className="absolute inset-0 h-full w-full object-cover"
                loading="eager"
                decoding="async"
              />
            </div>
          )}
        </div>

        <div className="container mx-auto max-w-5xl px-6">
          <Breadcrumbs
            items={[
              { name: "Home", url: "/" },
              { name: "Travel Guide", url: "/blog" },
              { name: category?.name || "Category", url: `/blog/category/${slug}` },
            ]}
            className="py-8"
          />
        </div>

        {/* Posts */}
        <section className="container mx-auto max-w-5xl px-6 pb-8">
          {isLoading ? (
            <div className="grid gap-px border border-sand-deep bg-sand-deep md:grid-cols-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="bg-shell p-8 md:p-10">
                  <SkeletonText lines={1} className="mb-5 h-3 w-24" />
                  <SkeletonText lines={1} className="mb-4 h-6 w-3/4" />
                  <SkeletonText lines={2} className="mb-8 w-full" />
                  <div className="flex justify-between border-t border-sand-deep pt-6">
                    <SkeletonText lines={1} className="h-3 w-24" />
                    <SkeletonText lines={1} className="h-3 w-12" />
                  </div>
                </div>
              ))}
            </div>
          ) : posts && posts.length > 0 ? (
            <div className="grid gap-px border border-sand-deep bg-sand-deep md:grid-cols-2">
              {posts.map((post) => (
                <BlogPostCard key={post.id} post={post} showCategory={false} />
              ))}
            </div>
          ) : (
            <div className="border border-sand-deep bg-sand/50 px-6 py-16 text-center">
              <h2 className="mb-3 font-serif text-luxury-xl font-normal tracking-tight text-forest">
                No Guides Yet
              </h2>
              <p className="mx-auto mb-8 max-w-[46ch] font-sans text-[15px] font-light leading-relaxed text-ink-soft">
                We're working on adding guides to this category. Check back soon.
              </p>
              <Link
                href="/blog"
                className="inline-flex min-h-[24px] items-center gap-2 border-b border-sand-deep pb-1 font-sans text-[11px] uppercase tracking-[0.24em] text-forest transition-colors hover:border-forest"
                data-testid="link-browse-all-guides"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
                Browse all guides
              </Link>
            </div>
          )}
        </section>

        {/* Other categories */}
        {otherCategories.length > 0 && (
          <section className="container mx-auto max-w-5xl px-6 pt-16">
            <div className="border-t border-sand-deep pt-12">
              <h2 className="mb-8 font-sans text-[11px] uppercase tracking-[0.3em] text-driftwood">
                Explore other categories
              </h2>
              <ul className="grid gap-px border border-sand-deep bg-sand-deep sm:grid-cols-2 lg:grid-cols-3">
                {otherCategories.map((cat) => (
                  <li key={cat.id} className="bg-shell">
                    <Link
                      href={`/blog/category/${cat.slug}`}
                      className="group flex h-full items-center justify-between gap-3 p-6 transition-colors hover:bg-sand"
                      data-testid={`link-category-${cat.slug}`}
                    >
                      <span
                        className="font-serif text-[17px] leading-snug text-forest transition-colors group-hover:text-ink"
                        data-testid={`text-category-${cat.slug}`}
                      >
                        {cat.name}
                      </span>
                      <ArrowRight className="h-4 w-4 shrink-0 text-driftwood transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}

        {/* Closing CTA */}
        <section className="container mx-auto max-w-5xl px-6 pt-16">
          <div className="border-t border-sand-deep py-16 text-center md:py-20">
            <span className="mb-5 inline-block font-sans text-[11px] uppercase tracking-[0.3em] text-driftwood">
              Ka'a'awa &middot; Windward Oahu
            </span>
            <h2 className="mx-auto mb-5 max-w-[18ch] font-serif text-[clamp(26px,4vw,42px)] font-normal leading-tight tracking-tight text-forest">
              Ready to experience Oahu?
            </h2>
            <p className="mx-auto mb-10 max-w-[52ch] font-sans text-[16px] font-light leading-relaxed text-ink-soft">
              Two oceanfront homes in Ka'a'awa on the windward coast. Book direct with no platform fees.
            </p>
            <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Link
                href={bookCta.href}
                data-primary-book-cta
                className="inline-flex items-center justify-center bg-sun px-8 py-4 font-sans tracking-wide text-shell transition-colors duration-300 hover:bg-sun-hover"
                data-testid="button-book-now"
              >
                Check availability
              </Link>
              <Link
                href="/properties"
                className="inline-flex min-h-[24px] items-center gap-2 border-b border-sand-deep pb-1 font-sans text-[11px] uppercase tracking-[0.24em] text-forest transition-colors hover:border-forest"
                data-testid="link-cta-stay"
              >
                See our homes
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </div>
        </section>
      </main>

      <StickyMobileBookCta />
      <Footer />
    </div>
    </>
  );
}
